<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Member Registration</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Register CBHI and Community Members" name="description" />
    <meta content="Kelvin Njuguna Ndungu" name="author" />
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Member Registration Form</h4>
                <div class="page-title-right d-flex align-items-center">
                  <!-- Current Date Display -->
                  <small class="text-muted me-3">
                    <i class="bx bx-calendar-check me-1"></i>
                    {{ currentDateDisplay }}
                  </small>

                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Member Registration</a></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div class="row justify-content-center align-items-center d-flex">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="btn-toolbar p-3 d-flex flex-column w-100" role="toolbar">
                    <h4 class="card-title text-uppercase mb-1">
                      Member Registration Form
                    </h4>
                    <p class="pb-0 mb-0">Register new members for CBHI insurance or community health services.</p>
                  </div>
                </div>

                <div class="card-body">
                  <div v-if="alert.success" class="alert alert-success" role="alert">
                    {{message}}
                  </div>
                  <div v-if="alert.info" class="alert alert-primary" role="alert">
                    {{message}}
                  </div>
                  <div v-if="alert.failed" class="alert alert-danger" role="alert">
                    {{message}}
                  </div>

                  <div class="row">
                    <!-- Registration Type Selection -->
                    <div class="col-12">
                      <div class="bg-primary bg-soft border-2 p-3 mb-4">
                        <div class="row">
                          <div class="col-md-6">
                            <label class="form-label fw-bold">Registration Type <strong class="text-danger">*</strong></label>
                            <select v-model="memberForm.enrollmentType" @change="onEnrollmentTypeChange" class="form-select" required>
                              <option value="cbhi_member">CBHI Member (with Health Insurance)</option>
                              <option value="community_member" class="d-none">Community Member (Health Services Only)</option>
                            </select>
                          </div>
                          <div class="col-md-6" v-if="memberForm.enrollmentType === 'community_member'">
                            <label class="form-label fw-bold">Household</label>
                            <select v-model="memberForm.householdId" class="form-select">
                              <option value="">Create New Household</option>
                              <option v-for="household in households" :key="household.household_id" :value="household.household_id">
                                {{ household.household_number }} - {{ household.head_name }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Enhanced Plan Selection Section - Only for CBHI Members -->
                    <template v-if="memberForm.enrollmentType === 'cbhi_member'">
                      <div class="col-12">
                        <div class="bg-warning bg-soft border-2 p-3 mb-4">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <h5 class="mb-1 fw-bold text-uppercase">
                                <i class="bx bx-shield-check me-2"></i>
                                Choose Your Health Insurance Plan
                              </h5>
                              <p class="mb-0 text-muted">Select a plan that best fits your family's healthcare needs and budget</p>
                            </div>
                            <div class="d-flex gap-2">
                              <button @click="showPlanComparison = !showPlanComparison"
                                      class="btn btn-outline-primary btn-sm">
                                <i class="bx bx-compare me-1"></i>
                                {{ showPlanComparison ? 'Hide' : 'Compare' }} Plans
                              </button>
                              <button @click="showPlanDetails = !showPlanDetails"
                                      class="btn btn-outline-info btn-sm">
                                <i class="bx bx-info-circle me-1"></i>
                                {{ showPlanDetails ? 'Hide' : 'Details' }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Enhanced DYNAMIC Plan Comparison Table -->
                      <div v-if="showPlanComparison && availablePlans.length > 0" class="col-12 mb-4">
                        <div class="card border">
                          <div class="card-header bg-light">
                            <h6 class="mb-0 fw-bold">
                              <i class="bx bx-compare me-2"></i>
                              Comprehensive Plan Comparison
                            </h6>
                          </div>
                          <div class="card-body">
                            <div class="table-responsive">
                              <table class="table table-hover table-bordered comparison-table">
                                <thead class="table-primary">
                                <tr>
                                  <th class="plan-feature-header">Plan Features</th>
                                  <th v-for="plan in availablePlans" :key="plan.plan_id" class="text-center plan-header">
                                    <div class="fw-bold">{{ plan.plan_name }}</div>
                                    <small class="text-white d-block">{{ plan.plan_code }}</small>
                                    <small class="plan-type-badge badge" :class="getPlanTypeBadgeClass(plan.plan_type)">
                                      {{ plan.plan_type.toUpperCase() }}
                                    </small>
                                    <small v-if="plan.is_free" class="plan-type-badge badge bg-success d-block mt-1">
                                      FREE PLAN
                                    </small>
                                  </th>
                                </tr>
                                </thead>
                                <tbody>
                                <!-- Pricing Information -->
                                <tr class="section-header">
                                  <td colspan="100%" class="bg-light fw-bold text-center">
                                    <i class="bx bx-money me-2"></i>PRICING STRUCTURE
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Primary Member Fee</strong></td>
                                  <td v-for="plan in availablePlans" :key="'primary-' + plan.plan_id" class="text-center">
                                    <span class="price-badge" :class="plan.is_free ? 'bg-success' : ''">
                                      {{ plan.is_free ? 'FREE' : `${formatCurrency(plan.primary_annual_amount)}` }}
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Additional Member Rate</strong></td>
                                  <td v-for="plan in availablePlans" :key="'additional-' + plan.plan_id" class="text-center">
                                    <span class="price-badge secondary" :class="plan.is_free ? 'bg-success' : ''">
                                      {{ plan.is_free ? 'FREE' : `${(parseFloat(plan.additional_member_rate) * 100).toFixed(0)}% of primary` }}
                                    </span>
                                    <div v-if="!plan.is_free" class="text-muted small mt-1">
                                      ({{ formatCurrency(parseFloat(plan.primary_annual_amount) * parseFloat(plan.additional_member_rate)) }} per additional member)
                                    </div>
                                  </td>
                                </tr>

                                <!-- Beneficiary Rules Section - DYNAMIC -->
                                <tr class="section-header">
                                  <td colspan="100%" class="bg-light fw-bold text-center">
                                    <i class="bx bx-group me-2"></i>BENEFICIARY RULES
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Free Spouses Included</strong></td>
                                  <td v-for="plan in availablePlans" :key="'spouses-' + plan.plan_id" class="text-center">
                                    <div class="coverage-text">
                                      {{ formatSpouseCount(getBeneficiaryRuleValue(plan, 'free_immediate_family', 'included_spouses')) }}
                                    </div>
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Children Free Until Age</strong></td>
                                  <td v-for="plan in availablePlans" :key="'children-age-' + plan.plan_id" class="text-center">
                                    <div class="coverage-text">
                                      {{ getBeneficiaryRuleValue(plan, 'free_immediate_family', 'children_under_age') }} years
                                    </div>
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Adult Children From Age</strong></td>
                                  <td v-for="plan in availablePlans" :key="'adult-age-' + plan.plan_id" class="text-center">
                                    <div class="coverage-text">
                                      {{ getBeneficiaryRuleValue(plan, 'additional_members', 'applies_to', 'adult_children_from_age') }} years
                                    </div>
                                  </td>
                                </tr>

                                <!-- Sample Pricing Scenarios -->
                                <tr class="section-header">
                                  <td colspan="100%" class="bg-light fw-bold text-center">
                                    <i class="bx bx-calculator me-2"></i>SAMPLE PRICING SCENARIOS
                                  </td>
                                </tr>
                                <tr v-for="(example, key) in getSampleScenarios()" :key="'scenario-' + key">
                                  <td class="feature-name"><strong>{{ example.label }}</strong></td>
                                  <td v-for="plan in availablePlans" :key="key + '-' + plan.plan_id" class="text-center">
                                    <div class="coverage-amount">
                                      {{ calculateScenarioCost(plan, example.calculation) }}
                                    </div>
                                  </td>
                                </tr>

                                <!-- Coverage Features - DYNAMIC -->
                                <tr class="section-header" v-if="allFeatures.length > 0">
                                  <td colspan="100%" class="bg-light fw-bold text-center">
                                    <i class="bx bx-check-shield me-2"></i>COVERAGE FEATURES
                                  </td>
                                </tr>
                                <tr v-for="feature in allFeatures" :key="'feature-' + feature.key">
                                  <td class="feature-name"><strong>{{ feature.label }}</strong></td>
                                  <td v-for="plan in availablePlans" :key="feature.key + '-' + plan.plan_id" class="text-center">
                                    <i class="coverage-icon" :class="getFeatureValue(plan, feature.key) ? 'bx bx-check-circle text-success' : 'bx bx-x-circle text-danger'"></i>
                                    <div class="coverage-text">{{ formatFeatureValue(getFeatureValue(plan, feature.key)) }}</div>
                                  </td>
                                </tr>

                                <!-- Eligibility -->
                                <tr class="section-header">
                                  <td colspan="100%" class="bg-light fw-bold text-center">
                                    <i class="bx bx-user-check me-2"></i>ELIGIBILITY
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Eligibility Requirements</strong></td>
                                  <td v-for="plan in availablePlans" :key="'eligibility-' + plan.plan_id" class="text-center">
                                    <span class="badge" :class="plan.is_free ? 'bg-warning text-dark' : 'bg-info'">
                                      {{ plan.eligibility || 'All eligible citizens' }}
                                    </span>
                                  </td>
                                </tr>

                                <!-- Select Plan Row -->
                                <tr class="section-header">
                                  <td colspan="100%" class="bg-light fw-bold text-center">
                                    <i class="bx bx-check-circle me-2"></i>PLAN SELECTION
                                  </td>
                                </tr>
                                <tr>
                                  <td class="feature-name"><strong>Select Plan</strong></td>
                                  <td v-for="plan in availablePlans" :key="'select-' + plan.plan_id" class="text-center">
                                    <button @click="selectPlan(plan)"
                                            class="btn btn-sm w-100 select-plan-btn"
                                            :class="memberForm.selectedPlanId === plan.plan_id ? 'btn-success text-white' : 'btn-outline-primary'">
                                      <i class="bx bx-check me-1 text-white" v-if="memberForm.selectedPlanId === plan.plan_id"></i>
                                      {{ memberForm.selectedPlanId === plan.plan_id ? 'Selected' : 'Select' }}
                                    </button>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Detailed Plan Information Cards (DYNAMIC) -->
                      <div v-if="showPlanDetails && availablePlans.length > 0" class="col-12 mb-4">
                        <div class="row">
                          <div v-for="plan in availablePlans" :key="'detail-' + plan.plan_id" class="col-xl-6 col-lg-12 mb-4">
                            <div class="card plan-detail-card h-100"
                                 :class="{ 'border-success shadow': memberForm.selectedPlanId === plan.plan_id }">
                              <div class="card-header"
                                   :class="memberForm.selectedPlanId === plan.plan_id ? 'bg-success text-white' : 'bg-light'">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h5 class="mb-1 fw-bold">{{ plan.plan_name }}</h5>
                                    <p class="mb-0" :class="memberForm.selectedPlanId === plan.plan_id ? 'text-white-75' : 'text-muted'">
                                      {{ plan.plan_description || 'Comprehensive health insurance coverage' }}
                                    </p>
                                  </div>
                                  <div>
                                    <span class="badge" :class="getPlanTypeBadgeClass(plan.plan_type)">
                                      {{ plan.plan_type.toUpperCase() }}
                                    </span>
                                    <span v-if="plan.is_free" class="badge bg-success d-block mt-1">
                                      FREE
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div class="card-body">
                                <!-- Pricing Summary -->
                                <div class="pricing-summary mb-4">
                                  <div class="row text-center">
                                    <div class="col-6">
                                      <div class="pricing-item">
                                        <h4 class="mb-1" :class="plan.is_free ? 'text-success' : 'text-primary'">
                                          {{ plan.is_free ? 'FREE' : formatCurrency(plan.primary_annual_amount) }}
                                        </h4>
                                        <small class="text-muted">Primary Member</small>
                                      </div>
                                    </div>
                                    <div class="col-6">
                                      <div class="pricing-item">
                                        <h5 class="mb-1" :class="plan.is_free ? 'text-success' : ''">
                                          {{ plan.is_free ? 'FREE' : `${(parseFloat(plan.additional_member_rate) * 100).toFixed(0)}%` }}
                                        </h5>
                                        <small class="text-muted">Additional Member Rate</small>
                                        <div v-if="!plan.is_free" class="text-muted" style="font-size: 0.75rem;">
                                          ({{ formatCurrency(parseFloat(plan.primary_annual_amount) * parseFloat(plan.additional_member_rate)) }})
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Beneficiary Rules Summary -->
                                <div v-if="plan.coverage_summary" class="alert alert-info mb-4">
                                  <div class="d-flex align-items-start">
                                    <i class="bx bx-info-circle fs-4 me-3"></i>
                                    <div>
                                      <h6 class="mb-1 fw-bold">Coverage Summary</h6>
                                      <p class="mb-0 small">{{ plan.coverage_summary }}</p>
                                    </div>
                                  </div>
                                </div>

                                <!-- Special Free Plan Notice -->
                                <div v-if="plan.is_free" class="alert alert-success mb-4">
                                  <div class="d-flex align-items-center">
                                    <i class="bx bx-gift fs-3 me-3"></i>
                                    <div>
                                      <h6 class="mb-1 fw-bold">Free Plan Benefits</h6>
                                      <p class="mb-0 small">
                                        This plan is completely free for eligible members. No premiums required for primary member or additional family members.
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <!-- Eligibility Notice -->
                                <div v-if="plan.eligibility" class="alert alert-info mb-4">
                                  <div class="d-flex align-items-center">
                                    <i class="bx bx-user-check fs-4 me-3"></i>
                                    <div>
                                      <h6 class="mb-1 fw-bold">Eligibility Requirement</h6>
                                      <p class="mb-0">{{ plan.eligibility }}</p>
                                    </div>
                                  </div>
                                </div>

                                <!-- Sample Pricing Examples from API -->
                                <div v-if="plan.examples" class="sample-premiums mb-4">
                                  <h6 class="fw-bold mb-3">Sample Family Scenarios:</h6>
                                  <div class="row">
                                    <div v-for="(example, key) in plan.examples" :key="key" class="col-12 mb-3">
                                      <div class="premium-example p-3 border rounded">
                                        <div class="premium-type fw-bold mb-2">{{ example.description }}</div>
                                        <div class="premium-amount text-primary fw-bold mb-1">
                                          Total: {{ plan.is_free ? 'FREE' : formatCurrency(example.total_cost) }}
                                        </div>
                                        <div class="premium-breakdown text-muted small">
                                          {{ example.breakdown }}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Plan Selection Button -->
                                <div class="text-center">
                                  <button @click="selectPlan(plan)"
                                          class="btn w-100"
                                          :class="memberForm.selectedPlanId === plan.plan_id ? 'btn-success text-white' : 'btn-outline-primary'">
                                    <i class="bx bx-check me-1" v-if="memberForm.selectedPlanId === plan.plan_id"></i>
                                    {{ memberForm.selectedPlanId === plan.plan_id ? 'Selected Plan' : 'Select This Plan' }}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Quick Plan Selection Cards (DYNAMIC) -->
                      <div v-if="!showPlanDetails && Array.isArray(availablePlans) && availablePlans.length > 0" class="col-12 mb-4">
                        <div class="row">
                          <div v-for="plan in availablePlans" :key="'quick-' + plan.plan_id" class="col-lg-6 col-xl-4 mb-3">
                            <div v-if="plan && plan.plan_id" class="card plan-card h-100"
                                 :class="{ 'border-success shadow-sm': memberForm.selectedPlanId === plan.plan_id }"
                                 @click="selectPlan(plan)"
                                 style="cursor: pointer;">
                              <div class="card-header text-center"
                                   :class="memberForm.selectedPlanId === plan.plan_id ? 'bg-success text-white' : 'bg-light'">
                                <div class="d-flex justify-content-between align-items-center">
                                  <h6 class="mb-0 fw-bold">{{ plan.plan_name || 'Unnamed Plan' }}</h6>
                                  <div>
                                    <i v-if="memberForm.selectedPlanId === plan.plan_id"
                                       class="bx bx-check-circle fs-4 text-white"></i>
                                    <span v-else class="badge" :class="getPlanTypeBadgeClass(plan.plan_type || 'standard')">
                                      {{ (plan.plan_type || 'standard').toUpperCase() }}
                                    </span>
                                  </div>
                                </div>
                                <small class="d-block mt-1"
                                       :class="memberForm.selectedPlanId === plan.plan_id ? 'text-white-50' : 'text-muted'">
                                  {{ plan.plan_description || 'Comprehensive coverage' }}
                                </small>
                                <span v-if="plan.is_free" class="badge bg-warning text-dark mt-2">
                                  FREE PLAN
                                </span>
                              </div>
                              <div class="card-body">
                                <div class="text-center mb-3">
                                  <div class="mb-2">
                                    <span class="h4 fw-bold" :class="plan.is_free ? 'text-success' : 'text-primary'">
                                      {{ plan.is_free ? 'FREE' : formatCurrency(plan.primary_annual_amount || 0) }}
                                    </span>
                                    <small class="text-muted d-block">Primary Member</small>
                                  </div>
                                  <div class="mb-2">
                                    <span class="h6 fw-bold" :class="plan.is_free ? 'text-success' : ''">
                                      {{ plan.is_free ? 'FREE' : `${(parseFloat(plan.additional_member_rate || 0) * 100).toFixed(0)}% Rate` }}
                                    </span>
                                    <small class="text-muted d-block">Additional Members</small>
                                  </div>

                                  <div v-if="plan.beneficiary_rules" class="mt-3 p-2 bg-light rounded">
                                    <small class="text-muted d-block fw-bold mb-1">Included Free:</small>
                                    <small class="text-muted d-block">
                                      {{ formatBeneficiaryRulesSummary(plan) }}
                                    </small>
                                  </div>

                                  <div v-if="plan.eligibility" class="badge bg-info mt-2">
                                    {{ plan.eligibility }}
                                  </div>
                                </div>

                                <div class="text-center mt-3">
                                  <button @click.stop="selectPlan(plan)"
                                          class="btn btn-sm w-100"
                                          :class="memberForm.selectedPlanId === plan.plan_id ? 'btn-success text-white' : 'btn-outline-primary'">
                                    <i class="bx bx-check me-1" v-if="memberForm.selectedPlanId === plan.plan_id"></i>
                                    {{ memberForm.selectedPlanId === plan.plan_id ? 'Selected Plan' : 'Select This Plan' }}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Loading State for Plans -->
                      <div v-if="planLoading" class="col-12 mb-4">
                        <div class="text-center p-5">
                          <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading plans...</span>
                          </div>
                          <p class="mt-3 text-muted">Loading available insurance plans...</p>
                        </div>
                      </div>

                      <!-- No Plans Available -->
                      <div v-if="!planLoading && availablePlans.length === 0" class="col-12 mb-4">
                        <div class="alert alert-info">
                          <i class="bx bx-info-circle me-2"></i>
                          <strong>No insurance plans are currently available.</strong> Please contact the administrator.
                        </div>
                      </div>

                      <!-- Plan Selection Required Alert -->
                      <div v-if="availablePlans.length > 0 && !memberForm.selectedPlanId" class="col-12 mb-4">
                        <div class="alert alert-warning">
                          <i class="bx bx-info-circle me-2"></i>
                          <strong>Please select a health insurance plan to continue with CBHI registration.</strong>
                        </div>
                      </div>

                      <!-- Selected Plan Summary -->
                      <div v-if="selectedPlan" class="col-12 mb-4">
                        <div class="card border-success">
                          <div class="card-header bg-success text-white">
                            <h6 class="mb-0 fw-bold" style="color: white">
                              <i class="bx bx-check-circle me-2"></i>
                              Selected Plan: {{ selectedPlan.plan_name }}
                              <span v-if="selectedPlan.is_free" class="badge bg-warning text-dark ms-2">FREE PLAN</span>
                            </h6>
                          </div>
                          <div class="card-body">
                            <div class="row">
                              <div class="col-md-8">
                                <p class="mb-2">{{ selectedPlan.plan_description || 'Comprehensive health insurance coverage' }}</p>
                                <div v-if="selectedPlan.coverage_summary" class="alert alert-info mb-2">
                                  <small>{{ selectedPlan.coverage_summary }}</small>
                                </div>
                                <div class="row">
                                  <div class="col-sm-6">
                                    <small class="text-muted d-block">Primary Member Fee</small>
                                    <strong :class="selectedPlan.is_free ? 'text-success' : ''">
                                      {{ selectedPlan.is_free ? 'FREE' : formatCurrency(selectedPlan.primary_annual_amount) }}
                                    </strong>
                                  </div>
                                  <div class="col-sm-6">
                                    <small class="text-muted d-block">Additional Member Rate</small>
                                    <strong :class="selectedPlan.is_free ? 'text-success' : ''">
                                      {{ selectedPlan.is_free ? 'FREE' : `${(parseFloat(selectedPlan.additional_member_rate) * 100).toFixed(0)}%` }}
                                    </strong>
                                  </div>
                                </div>
                                <div v-if="selectedPlan.eligibility" class="mt-2">
                                  <small class="text-muted d-block">Eligibility</small>
                                  <span class="badge bg-info">{{ selectedPlan.eligibility }}</span>
                                </div>
                              </div>
                              <div class="col-md-4">
                                <div class="text-end">
                                  <button @click="clearPlanSelection"
                                          class="btn btn-outline-secondary btn-sm">
                                    <i class="bx bx-x me-1"></i>
                                    Change Plan
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Primary Member Information Section - Only show after plan selection -->
                    <template v-if="(memberForm.enrollmentType === 'cbhi_member' && memberForm.selectedPlanId && selectedPlan) || memberForm.enrollmentType === 'community_member'">
                      <div class="col-12">
                        <div class="bg-info border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                          <p class="fw-semibold mb-0 pb-0 text-uppercase">
                            Primary Member Information
                          </p>
                        </div>
                      </div>

                      <!-- CBHI Card Issuance Section - Only for CBHI Members -->
                      <div v-if="memberForm.enrollmentType === 'cbhi_member'" class="col-12">
                        <div class="bg-secondary bg-soft border-2 p-3 mb-4">
                          <div class="row align-items-center">
                            <div class="col-md-6">
                              <div class="form-check form-switch">
                                <input v-model="memberForm.cbhiCardIssued"
                                       class="form-check-input"
                                       type="checkbox"
                                       id="cbhiCardSwitch">
                                <label class="form-check-label fw-bold" for="cbhiCardSwitch">
                                  <i class="bx bx-id-card me-2"></i>
                                  Issue Physical CBHI Card
                                </label>
                              </div>
                              <small class="text-muted d-block mt-1">
                                Check this if you are issuing a physical CBHI card to the member
                              </small>
                            </div>
                            <div v-if="memberForm.cbhiCardIssued" class="col-md-6">
                              <label class="form-label">Card Issue Notes (Optional)</label>
                              <input v-model="memberForm.cardIssueNotes"
                                     type="text"
                                     class="form-control"
                                     placeholder="e.g., Card number, batch, etc.">
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Split Name Fields for Primary Member -->
                      <div class="col-12">
                        <div class="row">
                          <div class="col-md-2 col-sm-6 mb-3">
                            <div class="mb-3">
                              <label for="title-input" class="form-label">Title <strong class="text-danger fw-bold">*</strong></label>
                              <select v-model="memberForm.title" @change="onPrimaryTitleChange" class="form-control" id="title-input" required>
                                <option value="">Select Title</option>
                                <option value="Mr">Mr</option>
                                <option value="Mrs">Mrs</option>
                                <option value="Miss">Miss</option>
                                <option value="Ms">Ms</option>
                                <option value="Dr">Dr</option>
                                <option value="Prof">Prof</option>
                                <option value="Mx">Mx</option>
                              </select>
                            </div>
                          </div>

                          <div class="col-md-4 col-sm-12 mb-3">
                            <div class="mb-3">
                              <label for="first-name-input" class="form-label">First Name <strong class="text-danger fw-bold">*</strong></label>
                              <input v-model="memberForm.firstName" @input="updateFullName" type="text" class="form-control" id="first-name-input" placeholder="Enter First Name" spellcheck="false" required>
                            </div>
                          </div>

                          <div class="col-md-3 col-sm-6 mb-3">
                            <div class="mb-3">
                              <label for="middle-name-input" class="form-label">Middle Name</label>
                              <input v-model="memberForm.middleName" @input="updateFullName" type="text" class="form-control" id="middle-name-input" placeholder="Enter Middle Name" spellcheck="false">
                            </div>
                          </div>

                          <div class="col-md-3 col-sm-6 mb-3">
                            <div class="mb-3">
                              <label for="last-name-input" class="form-label">Last Name <strong class="text-danger fw-bold">*</strong></label>
                              <input v-model="memberForm.lastName" @input="updateFullName" type="text" class="form-control" id="last-name-input" placeholder="Enter Last Name" spellcheck="false" required>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Date of Birth -->
                      <div class="col-sm-12 col-md-6 col-lg-4 mb-3">
                        <div class="mb-3">
                          <label class="form-label">Date Of Birth <strong class="text-danger fw-bold">*</strong></label>
                          <input
                              v-model="memberForm.dateOfBirth"
                              @change="validatePrimaryMemberAge"
                              type="date"
                              class="form-control"
                              :max="getPrimaryMemberMaxDate()"
                              :min="getPrimaryMemberMinDate()"
                              required>
                          <small v-if="primaryMemberAgeWarning" :class="primaryMemberAgeClass">
                            <i class="bx bx-error-circle"></i> {{ primaryMemberAgeWarning }}
                          </small>
                          <small v-else class="text-muted">
                            <i class="bx bx-info-circle"></i> Primary member must be at least 18 years old
                          </small>
                        </div>
                      </div>

                      <!-- ID Number Field - MANDATORY for CBHI, OPTIONAL for Community -->
                      <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                        <div class="mb-3">
                          <label for="national-id-input" class="form-label">
                            ID Number
                            <strong v-if="memberForm.enrollmentType === 'cbhi_member'" class="text-danger fw-bold">*</strong>
                          </label>
                          <input
                              v-model="memberForm.nationalId"
                              type="text"
                              class="form-control"
                              id="national-id-input"
                              :placeholder="memberForm.enrollmentType === 'cbhi_member' ? 'Passport/National ID/Driver\'s License (required)' : 'Passport/National ID/Driver\'s License (optional)'"
                              spellcheck="false"
                              :required="memberForm.enrollmentType === 'cbhi_member'">
                          <small class="text-muted">
                            <i class="bx bx-info-circle"></i>
                            {{ memberForm.enrollmentType === 'cbhi_member' ? 'ID Number is required for CBHI members' : 'ID Number is optional' }}
                          </small>
                        </div>
                      </div>

                      <div class="col-md-4 col-sm-6 mb-3">
                        <div class="mb-3">
                          <label for="gender-input" class="form-label">Gender <strong class="text-danger fw-bold">*</strong></label>
                          <select v-model="memberForm.gender" @change="onPrimaryGenderChange" class="form-select" id="gender-input" required>
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                        </div>
                      </div>

                      <!-- Occupation Field -->
                      <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                        <div class="mb-3">
                          <label for="occupation-input" class="form-label">Occupation</label>
                          <input v-model="memberForm.occupation"
                                 type="text"
                                 class="form-control"
                                 id="occupation-input"
                                 placeholder="Enter Occupation (optional)"
                                 spellcheck="false">
                          <small class="text-muted">
                            <i class="bx bx-info-circle"></i> Occupation is optional
                          </small>
                        </div>
                      </div>

                      <!-- Location fields -->
                      <div class="col-sm-6 col-md-3 mb-3">
                        <div class="mb-3">
                          <label for="county-input" class="form-label">County <strong class="text-danger fw-bold">*</strong></label>
                          <select v-model="memberForm.countyId" @change="onCountyChange" class="form-select" id="county-input" required>
                            <option value="">Select County</option>
                            <option v-for="county in counties" :key="county.county_id" :value="county.county_id">
                              {{ county.county_name }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="col-sm-6 col-md-3 mb-3">
                        <div class="mb-3">
                          <label for="subcounty-input" class="form-label">Sub-County <strong class="text-danger fw-bold">*</strong></label>
                          <select v-model="memberForm.subCountyId" @change="onSubCountyChange" class="form-select" id="subcounty-input" required :disabled="!memberForm.countyId">
                            <option value="">Select Sub-County</option>
                            <option v-for="subCounty in subCounties" :key="subCounty.sub_county_id" :value="subCounty.sub_county_id">
                              {{ subCounty.sub_county_name }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="col-sm-6 col-md-3 mb-3">
                        <div class="mb-3">
                          <label for="ward-input" class="form-label">Ward <strong class="text-danger fw-bold">*</strong></label>
                          <select v-model="memberForm.wardId" @change="onWardChange" class="form-select" id="ward-input" required :disabled="!memberForm.subCountyId">
                            <option value="">Select Ward</option>
                            <option v-for="ward in wards" :key="ward.ward_id" :value="ward.ward_id">
                              {{ ward.ward_name }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="col-sm-6 col-md-3 mb-3">
                        <div class="mb-3">
                          <label for="location-input" class="form-label">Location</label>
                          <select v-model="memberForm.locationId" @change="onLocationChange" class="form-select" id="location-input" :disabled="!memberForm.wardId">
                            <option value="">Select Location</option>
                            <option v-for="location in locations" :key="location.location_id" :value="location.location_id">
                              {{ location.location_name }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                        <div class="mb-3">
                          <label for="address-input" class="form-label">Address <strong class="text-danger fw-bold">*</strong></label>
                          <input v-model="memberForm.address" type="text" class="form-control" id="address-input" placeholder="Enter address (e.g., village or town name)" spellcheck="false" required>
                        </div>
                      </div>

                      <!-- Email field - Optional -->
                      <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                        <div class="mb-3">
                          <label for="email-input" class="form-label">Email Address</label>
                          <input
                              v-model="memberForm.email"
                              type="email"
                              class="form-control"
                              id="email-input"
                              placeholder="Enter Email Address (optional)"
                              spellcheck="false">
                          <small class="text-muted">
                            <i class="bx bx-info-circle"></i> Email is optional
                          </small>
                        </div>
                      </div>

                      <!-- Phone Number Field - Generic -->
                      <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                        <div class="mb-3">
                          <label for="phone-input" class="form-label">Phone Number</label>
                          <input
                              v-model="memberForm.phoneNumber"
                              type="tel"
                              class="form-control"
                              id="phone-input"
                              placeholder="e.g., 0712345678 (optional)"
                              spellcheck="false">
                          <small class="text-muted">
                            <i class="bx bx-info-circle"></i> Phone number is optional
                          </small>
                        </div>
                      </div>

                      <!-- Beneficiaries Section - Only for CBHI Members -->
                      <template v-if="memberForm.enrollmentType === 'cbhi_member' && selectedPlan">
                        <div class="col-12">
                          <div class="bg-info bg-soft border-2 p-3 mb-4 d-flex align-items-center">
                            <p class="fw-semibold mb-0 text-uppercase text-dark">Beneficiaries</p>
                          </div>
                        </div>

                        <div class="col-12 mb-2">
                          <div class="alert alert-success border-2" role="alert">
                            <h4 class="alert-heading">Beneficiaries Registration Guide</h4>
                            <p>You are registering beneficiaries under a primary member. Please ensure that only eligible family members are included.</p>
                            <hr>
                            <p class="mb-0">
                              <strong>{{ selectedPlan.plan_name }} Premium Information:</strong><br>
                              <span v-if="selectedPlan.is_free">
                                - <strong class="text-success">This is a FREE plan</strong> - No premiums required for primary member or beneficiaries<br>
                                - <strong>Eligibility:</strong> {{ selectedPlan.eligibility }}<br>
                              </span>
                              <span v-else>
                                - <strong>Primary Member Fee:</strong> {{ formatCurrency(selectedPlan.primary_annual_amount) }}<br>
                                - <strong>Immediate Family (FREE):</strong> {{ formatImmediateFamilyRules(selectedPlan) }}<br>
                                - <strong>Additional Members:</strong> {{ (parseFloat(selectedPlan.additional_member_rate) * 100).toFixed(0) }}% of primary fee ({{ formatCurrency(parseFloat(selectedPlan.primary_annual_amount) * parseFloat(selectedPlan.additional_member_rate)) }} each)<br>
                              </span>
                              <br>
                              <strong>Eligible beneficiaries include:</strong><br>
                              - <strong>Spouse:</strong> Must be 18 years or older <span class="text-info">({{ getSpouseRuleText(selectedPlan) }}, ID number required)</span><br>
                              - <strong>Children:</strong> Any age allowed (children under {{ getChildAgeThreshold(selectedPlan) }} are free, those {{ getChildAgeThreshold(selectedPlan) }}+ are charged, ID number optional for children only)<br>
                              - <strong>Parents/Grandparents:</strong> Must be 18 years or older (ID number required)<br>
                              - <strong>Siblings:</strong> Any age allowed (ID number required for 18+ siblings)<br>
                              <br>
                              <strong class="text-primary">⭐ SPECIAL CASES for Adult Children ({{ getChildAgeThreshold(selectedPlan) }}+ years):</strong><br>
                              Adult children are normally charged as additional members. However, they qualify as <strong class="text-success">FREE immediate family</strong> if:<br>
                              - They have a <strong>disability</strong>, OR<br>
                              - They don't have their own income (still <strong>dependent/living at home</strong>)
                            </p>
                          </div>
                        </div>

                        <!-- Beneficiaries form fields -->
                        <div class="col-12" v-for="(beneficiary, index) in beneficiaries" :key="index">
                          <div class="py-3 px-4 rounded-1 border border-dashed border-gray-5 mb-4">
                            <div class="col-12">
                              <div class="d-flex align-items-center justify-content-between">
                                <h6 class="fs-15 fw-bolder mb-0 pb-0">
                                  Beneficiary Number {{ index + 1 }}
                                  <span v-if="!selectedPlan.is_free && isImmediateFamilyWithSpecialCase(beneficiary, index)" class="badge bg-success ms-2">Free - Immediate Family</span>
                                  <span v-else-if="!selectedPlan.is_free && isAdultChildQualifyingForFree(beneficiary)" class="badge bg-info ms-2">Free - Special Case</span>
                                  <span v-else-if="!selectedPlan.is_free" class="badge bg-warning ms-2">
                                    {{ formatCurrency(parseFloat(selectedPlan.primary_annual_amount) * parseFloat(selectedPlan.additional_member_rate)) }}
                                  </span>
                                  <span v-else class="badge bg-success ms-2">Free</span>
                                </h6>
                                <button v-if="beneficiaries.length > 1" @click="removeBeneficiary(index)" class="btn-sm btn-danger btn text-uppercase">Remove Beneficiary</button>
                              </div>
                            </div>
                            <hr class="mb-3">
                            <div class="row">
                              <!-- Split Name Fields for Beneficiary -->
                              <div class="col-sm-6 col-md-2">
                                <div class="mb-3">
                                  <label class="form-label">Title <strong class="text-danger fw-bold">*</strong></label>
                                  <select v-model="beneficiary.title" @change="onBeneficiaryTitleChange(index)" class="form-control" required>
                                    <option value="">Select</option>
                                    <option value="Mr">Mr</option>
                                    <option value="Mrs">Mrs</option>
                                    <option value="Miss">Miss</option>
                                    <option value="Ms">Ms</option>
                                    <option value="Dr">Dr</option>
                                    <option value="Prof">Prof</option>
                                    <option value="Mx">Mx</option>
                                  </select>
                                </div>
                              </div>

                              <div class="col-sm-12 col-md-3">
                                <div class="mb-3">
                                  <label class="form-label">First Name <strong class="text-danger fw-bold">*</strong></label>
                                  <input v-model="beneficiary.firstName" @input="updateBeneficiaryFullName(index)" type="text" class="form-control" placeholder="Enter First Name" required>
                                </div>
                              </div>

                              <div class="col-sm-6 col-md-2">
                                <div class="mb-3">
                                  <label class="form-label">Middle Name</label>
                                  <input v-model="beneficiary.middleName" @input="updateBeneficiaryFullName(index)" type="text" class="form-control" placeholder="Enter Middle Name">
                                </div>
                              </div>

                              <div class="col-sm-6 col-md-2">
                                <div class="mb-3">
                                  <label class="form-label">Last Name <strong class="text-danger fw-bold">*</strong></label>
                                  <input v-model="beneficiary.lastName" @input="updateBeneficiaryFullName(index)" type="text" class="form-control" placeholder="Enter Last Name" required>
                                </div>
                              </div>

                              <div class="col-md-3 col-sm-6">
                                <div class="mb-3">
                                  <label class="form-label">Relationship <strong class="text-danger fw-bold">*</strong></label>
                                  <select v-model="beneficiary.relationship"
                                          @change="onBeneficiaryRelationshipChange(index)"
                                          class="form-select"
                                          :class="{'is-invalid': beneficiary.relationshipError}"
                                          required>
                                    <option value="">Select Relationship</option>
                                    <option value="spouse">Spouse</option>
                                    <option value="child">Child</option>
                                    <option value="parent">Parent</option>
                                    <option value="sibling">Sibling</option>
                                    <option value="grandparent">Grandparent</option>
                                    <option value="grandchild">Grandchild</option>
                                    <option value="other">Other</option>
                                  </select>
                                  <small class="text-danger" v-if="beneficiary.relationshipError">
                                    <i class="bx bx-error-circle"></i> {{ beneficiary.relationshipError }}
                                  </small>
                                  <small class="text-info" v-else-if="getAgeRequirement(beneficiary.relationship)">
                                    <i class="bx bx-info-circle"></i> {{ getAgeRequirement(beneficiary.relationship) }}
                                  </small>
                                </div>
                              </div>

                              <div class="col-md-2 col-sm-6">
                                <div class="mb-3">
                                  <label class="form-label">Gender <strong class="text-danger fw-bold">*</strong></label>
                                  <select v-model="beneficiary.gender" @change="onBeneficiaryGenderChange(index)" class="form-select" required>
                                    <option value="">Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                  </select>
                                </div>
                              </div>

                              <!-- Date of Birth for Beneficiary -->
                              <div class="col-sm-12 col-md-4">
                                <div class="mb-3">
                                  <label class="form-label">Date of Birth <strong class="text-danger fw-bold">*</strong></label>
                                  <input
                                      v-model="beneficiary.dateOfBirth"
                                      @change="validateBeneficiaryAge(index)"
                                      type="date"
                                      class="form-control"
                                      :max="getMaxDate(beneficiary.relationship)"
                                      :min="getMinDate(beneficiary.relationship)"
                                      required>
                                  <small v-if="beneficiary.ageWarning" class="text-danger d-block">
                                    {{ beneficiary.ageWarning }}
                                  </small>
                                </div>
                              </div>

                              <!-- ID Number Field - MANDATORY except for children -->
                              <div class="col-sm-6 col-md-3">
                                <div class="mb-3">
                                  <label class="form-label">
                                    ID Number
                                    <strong v-if="beneficiary.relationship !== 'child'" class="text-danger fw-bold">*</strong>
                                  </label>
                                  <input
                                      v-model="beneficiary.idNumber"
                                      type="text"
                                      class="form-control"
                                      :placeholder="beneficiary.relationship === 'child' ? 'Enter ID Number (optional)' : 'Enter ID Number (required)'"
                                      :required="beneficiary.relationship !== 'child'">
                                  <small class="text-muted">
                                    <i class="bx bx-info-circle"></i>
                                    {{ beneficiary.relationship === 'child' ? 'ID Number is optional for children' : 'ID Number is required' }}
                                  </small>
                                </div>
                              </div>

                              <!-- Disability and Dependency Fields for Adult Children -->
                              <template v-if="beneficiary.relationship === 'child' && isAdultChild(beneficiary)">
                                <div class="col-12">
                                  <div class="alert alert-info bg-soft border-info">
                                    <h6 class="fw-bold mb-2">
                                      <i class="bx bx-info-circle me-2"></i>
                                      Special Cases for Adult Children ({{ getChildAgeThreshold(selectedPlan) }}+)
                                    </h6>
                                    <p class="mb-2 small">
                                      Adult children ({{ getChildAgeThreshold(selectedPlan) }}+ years) are normally charged as additional members. However, they qualify as <strong>FREE immediate family</strong> if:
                                    </p>
                                    <ul class="mb-0 small">
                                      <li>They have a disability, OR</li>
                                      <li>They don't have their own income (still dependent/living at home)</li>
                                    </ul>
                                  </div>
                                </div>

                                <!-- Disability Section -->
                                <div class="col-md-6">
                                  <div class="mb-3">
                                    <div class="card border-primary">
                                      <div class="card-body">
                                        <div class="form-check form-switch mb-3">
                                          <input
                                              v-model="beneficiary.hasDisability"
                                              @change="onDisabilityStatusChange(index)"
                                              class="form-check-input"
                                              type="checkbox"
                                              :id="'disabilitySwitch-' + index">
                                          <label class="form-check-label fw-bold" :for="'disabilitySwitch-' + index">
                                            <i class="bx bx-health me-2"></i>
                                            Has Disability
                                          </label>
                                        </div>

                                        <div v-if="beneficiary.hasDisability" class="mt-2">
                                          <label class="form-label">Disability Details <strong class="text-danger fw-bold">*</strong></label>
                                          <textarea
                                              v-model="beneficiary.disabilityNotes"
                                              class="form-control"
                                              rows="3"
                                              placeholder="Please describe the disability..."
                                              :required="beneficiary.hasDisability"></textarea>
                                          <small class="text-muted">
                                            <i class="bx bx-info-circle"></i> Provide details about the condition
                                          </small>

                                          <div v-if="beneficiary.hasDisability" class="alert alert-success mt-2 mb-0">
                                            <i class="bx bx-check-circle me-2"></i>
                                            <strong>This child qualifies as FREE immediate family member</strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Dependency Section -->
                                <div class="col-md-6">
                                  <div class="mb-3">
                                    <div class="card border-success">
                                      <div class="card-body">
                                        <div class="form-check form-switch mb-3">
                                          <input
                                              v-model="beneficiary.isDependent"
                                              @change="onDependencyStatusChange(index)"
                                              class="form-check-input"
                                              type="checkbox"
                                              :id="'dependentSwitch-' + index">
                                          <label class="form-check-label fw-bold" :for="'dependentSwitch-' + index">
                                            <i class="bx bx-home me-2"></i>
                                            Is Dependent (No Own Income)
                                          </label>
                                        </div>

                                        <div v-if="beneficiary.isDependent" class="mt-2">
                                          <label class="form-label">Dependency Details</label>
                                          <textarea
                                              v-model="beneficiary.dependencyNotes"
                                              class="form-control"
                                              rows="3"
                                              placeholder="Additional notes (e.g., student, living at home, etc.)"></textarea>
                                          <small class="text-muted">
                                            <i class="bx bx-info-circle"></i> Optional: Provide additional context
                                          </small>

                                          <div v-if="beneficiary.isDependent" class="alert alert-success mt-2 mb-0">
                                            <i class="bx bx-check-circle me-2"></i>
                                            <strong>This child qualifies as FREE immediate family member</strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Warning if neither is checked -->
                                <div v-if="!beneficiary.hasDisability && !beneficiary.isDependent" class="col-12">
                                  <div class="alert alert-warning">
                                    <i class="bx bx-error-circle me-2"></i>
                                    <strong>Note:</strong> This adult child ({{ getChildAgeThreshold(selectedPlan) }}+) will be charged as an additional member at
                                    <strong>{{ formatCurrency(parseFloat(selectedPlan.primary_annual_amount) * parseFloat(selectedPlan.additional_member_rate)) }}</strong>
                                    unless they have a disability or are dependent without own income.
                                  </div>
                                </div>
                              </template>

                            </div>
                          </div>
                        </div>

                        <div class="col-12">
                          <button @click="addBeneficiary"
                                  type="button"
                                  class="btn btn-outline-secondary w-100 text-center mb-3 waves-effect waves-light"
                                  aria-label="Add Beneficiary">
                            <i class="bx bx-user-plus font-size-16 align-middle me-2"></i>
                            Add Beneficiary
                          </button>
                        </div>
                      </template>
                    </template>
                  </div>
                </div>

                <!-- Payment Footer - Only for CBHI Members -->
                <div v-if="memberForm.enrollmentType === 'cbhi_member' && selectedPlan" class="card-footer bg-light border-top py-3 border border-dashed border-info d-flex flex-wrap">
                  <div class="row w-100">
                    <div class="col-md-6 mb-3">
                      <span class="fw-semibold text-dark d-block">Annual Premium</span>
                      <h4 class="amount fs-18 fw-bold my-1 d-block" :class="selectedPlan.is_free ? 'text-success' : ''">
                        {{ selectedPlan.is_free ? 'FREE' : formatCurrency(annualPremium) }}
                      </h4>
                      <span class="deals fs-12 text-muted d-block">
                        {{ totalBeneficiaries }} total members
                        <span v-if="!selectedPlan.is_free && filteredBeneficiaries.length > 0">
                          ({{ immediateFamilyCount }} immediate family, {{ additionalMemberCount }} additional)
                        </span>
                      </span>
                    </div>

                    <div class="col-md-6 mb-3">
                      <span class="fw-semibold text-dark d-block">Selected Plan</span>
                      <h6 class="amount fs-16 fw-bold my-1 d-block">
                        {{ selectedPlan.plan_name }}
                        <span v-if="selectedPlan.is_free" class="badge bg-success ms-2">FREE</span>
                      </h6>
                      <span class="deals fs-12 text-muted d-block">{{ selectedPlan.plan_description }}</span>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-white border-top py-3">
                  <div class="col-12">
                    <button @click="validateAndSubmit" type="button" class="btn btn-primary waves-effect waves-light text-uppercase" :disabled="loading || !isFormValid">
                      <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      <i v-else class="mdi mdi-check-circle-outline font-size-16 align-middle me-2"></i>
                      {{ loading ? 'Processing...' : (memberForm.enrollmentType === 'cbhi_member' ? (selectedPlan && selectedPlan.is_free ? 'Complete Registration' : 'Register to Payment') : 'Register Member') }}
                    </button>

                    <button @click="clearForm" type="button" class="btn btn-outline-secondary waves-effect text-uppercase mx-3">
                      <i class="mdi mdi-close font-size-16 align-middle me-2"></i> Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Payment Modal Component -->
      <PaymentModal
          ref="paymentModal"
          :member-info="registrationResult"
          :annual-premium="annualPremium"
          :total-beneficiaries="totalBeneficiaries"
          @payment-recorded="onPaymentRecorded"
          @payment-error="onPaymentError"
          @download-receipt="onDownloadReceipt"
      />

      <!-- Receipt Modal Component -->
      <ReceiptModal
          ref="receiptModal"
          :member-info="registrationResult"
          :total-beneficiaries="totalBeneficiaries"
          :expected-annual-premium="annualPremium"
          :total-paid-for-year="0"
          @modal-closed="onReceiptClosed"
      />

      <!-- Success Modal for Community Members and Free Plans -->
      <div v-if="showSuccessModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-body">
              <div class="text-center py-5">
                <div class="mb-4">
                  <span class="mdi mdi-check-circle-outline text-success display-2"></span>
                </div>
                <h4 class="fw-bold">
                  {{ memberForm.enrollmentType === 'community_member' ? 'Community Member Registered Successfully' : 'Member Registered Successfully' }}
                </h4>
                <p class="text-muted mb-2">
                  The member has been successfully registered.
                </p>
                <p class="text-muted">
                  <strong>Member ID:</strong> <span>{{ registrationResult.memberId }}</span>
                </p>
                <p v-if="registrationResult.cbhiNumber" class="text-muted">
                  <strong>CBHI Number:</strong> <span>{{ registrationResult.cbhiNumber }}</span>
                </p>
                <p class="text-muted mb-4">
                  {{ memberForm.enrollmentType === 'community_member' ?
                    'The member can now receive health services from Health Extension Workers.' :
                    'The member has been registered with a free plan and can now access health services.' }}
                </p>
                <div class="d-flex gap-3 justify-content-center">
                  <button class="btn btn-outline-secondary" @click="closeSuccessModal">Close</button>
                  <button class="btn btn-primary" @click="registerAnother">Register Another</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>


<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import PaymentModal from "@/components/cbhi-member-management/modals/PaymentModal.vue";
import ReceiptModal from "@/components/cbhi-member-management/modals/ReceiptModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "MembersAdd",
  components: {
    NavigationBar,
    PaymentModal,
    ReceiptModal
  },
  data() {
    return {
      loading: false,
      planLoading: false,
      paymentCompleted: false,

      // Currency configuration (can be changed per deployment)
      currencyCode: 'KES',
      currencySymbol: 'KES',

      availablePlans: [],
      selectedPlan: null,
      showPlanComparison: false,
      showPlanDetails: false,

      tempRegistrationData: null,

      memberForm: {
        enrollmentType: 'cbhi_member',
        selectedPlanId: null,
        householdId: '',
        relationshipToHead: 'head',
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        fullName: '',
        dateOfBirth: '',
        nationalId: '',
        gender: '',
        countyId: '',
        subCountyId: '',
        wardId: '',
        locationId: '',
        address: '',
        email: '',
        phoneNumber: '',
        maritalStatus: 'single',
        occupation: '',
        monthlyIncome: 0,
        householdSize: 1,
        isHEWRegistration: false,
        cbhiCardIssued: false,
        cardIssueNotes: ''
      },

      primaryMemberAgeWarning: '',
      primaryMemberAgeClass: 'text-muted',

      beneficiaries: [],

      counties: [],
      subCounties: [],
      wards: [],
      locations: [],
      households: [],

      showSuccessModal: false,

      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',

      registrationResult: {}
    };
  },

  computed: {
    currentDateDisplay() {
      const today = new Date();
      return today.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    allFeatures() {
      const featureSet = new Set();

      if (Array.isArray(this.availablePlans)) {
        this.availablePlans.forEach(plan => {
          if (plan && plan.features && typeof plan.features === 'object') {
            Object.keys(plan.features).forEach(key => {
              if (key !== 'children_coverage' && key !== 'free_spouses_count' && key !== 'additional_rate_percentage') {
                featureSet.add(key);
              }
            });
          }
        });
      }

      const featureMapping = {
        'unlimited_services': 'Unlimited Services',
        'all_hospitals': 'All Hospitals',
        'no_coverage_limit': 'No Coverage Limit',
        'immediate_family_free': 'Immediate Family Free',
        'additional_members_allowed': 'Additional Members Allowed',
        'emergency_services': 'Emergency Services',
        'dental_care': 'Dental Care',
        'vision_care': 'Vision Care',
        'maternity_care': 'Maternity Care',
        'mental_health': 'Mental Health',
        'chronic_care': 'Chronic Care',
        'preventive_care': 'Preventive Care',
        'rehabilitation': 'Rehabilitation',
        'pharmacy_benefits': 'Pharmacy Benefits',
        'specialist_care': 'Specialist Care',
        'surgery': 'Surgical Procedures',
        'hospitalization': 'Hospitalization',
        'laboratory': 'Laboratory Services',
        'radiology': 'Radiology/Imaging',
        'physiotherapy': 'Physiotherapy'
      };

      return Array.from(featureSet).map(key => ({
        key: key,
        label: featureMapping[key] || this.formatFeatureName(key)
      })).sort((a, b) => a.label.localeCompare(b.label));
    },

    totalBeneficiaries() {
      if (this.memberForm.enrollmentType === 'community_member') {
        return 1;
      }
      return this.filteredBeneficiaries.length + 1;
    },

    filteredBeneficiaries() {
      return this.beneficiaries.filter(b =>
          b.fullName || (b.firstName && b.lastName)
      );
    },

    immediateFamilyCount() {
      return this.getImmediateFamilyCountEnhanced();
    },

    additionalMemberCount() {
      return this.getAdditionalMemberCountEnhanced();
    },

    annualPremium() {
      if (this.memberForm.enrollmentType === 'community_member') {
        return 0;
      }

      if (!this.selectedPlan) {
        return 0;
      }

      if (this.selectedPlan.is_free) {
        return 0;
      }

      const primaryAmount = parseFloat(this.selectedPlan.primary_annual_amount) || 0;
      const additionalRate = parseFloat(this.selectedPlan.additional_member_rate) || 0;

      const additionalMemberCost = this.additionalMemberCount * (primaryAmount * additionalRate);

      return primaryAmount + additionalMemberCost;
    },

    isPrimaryMemberAgeValid() {
      if (!this.memberForm.dateOfBirth) {
        return false;
      }
      const age = this.calculateAge(this.memberForm.dateOfBirth);
      return age >= 18 && age <= 120;
    },

    isFormValid() {
      const requiredFieldsValid = this.memberForm.title &&
          this.memberForm.firstName &&
          this.memberForm.lastName &&
          this.memberForm.dateOfBirth &&
          this.memberForm.gender &&
          this.memberForm.countyId &&
          this.memberForm.subCountyId &&
          this.memberForm.wardId &&
          this.memberForm.address;

      const planSelectionValid = this.memberForm.enrollmentType === 'community_member' ||
          (this.memberForm.enrollmentType === 'cbhi_member' && this.memberForm.selectedPlanId);

      const ageValid = this.isPrimaryMemberAgeValid;

      let primaryDobValid = true;
      if (this.memberForm.dateOfBirth) {
        const primaryDob = new Date(this.memberForm.dateOfBirth);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        primaryDob.setHours(0, 0, 0, 0);
        primaryDobValid = primaryDob <= today;
      }

      const primaryIdValid = this.memberForm.enrollmentType === 'community_member' ||
          (this.memberForm.enrollmentType === 'cbhi_member' && this.memberForm.nationalId && this.memberForm.nationalId.trim() !== '');

      const primaryGenderTitleValid = this.validateGenderSalutationMatch(
          this.memberForm.gender,
          this.memberForm.title,
          'primary member'
      ).valid;

      const spouseLimitValid = !this.beneficiaries.some(b => b.relationshipError);

      const beneficiariesValid = this.beneficiaries.every((beneficiary, index) => {
        if (beneficiary.firstName || beneficiary.lastName || beneficiary.fullName) {
          if (!beneficiary.title || !beneficiary.firstName ||
              !beneficiary.lastName ||
              !beneficiary.gender || !beneficiary.dateOfBirth || !beneficiary.relationship) {
            return false;
          }

          const beneficiaryDob = new Date(beneficiary.dateOfBirth);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          beneficiaryDob.setHours(0, 0, 0, 0);

          if (beneficiaryDob > today) {
            return false;
          }

          const age = this.calculateAge(beneficiary.dateOfBirth);
          if (age < 0 || age > 120) {
            return false;
          }

          if (this.isAdultRelationship(beneficiary.relationship) && age < 18) {
            return false;
          }

          if (!this.validateGenderSalutationMatch(
              beneficiary.gender,
              beneficiary.title,
              `beneficiary ${index + 1}`
          ).valid) {
            return false;
          }

          if (this.memberForm.enrollmentType === 'cbhi_member' &&
              beneficiary.relationship !== 'child' &&
              (!beneficiary.idNumber || beneficiary.idNumber.trim() === '')) {
            return false;
          }

          if (beneficiary.hasDisability && (!beneficiary.disabilityNotes || beneficiary.disabilityNotes.trim() === '')) {
            return false;
          }

          if (beneficiary.ageWarning) {
            return false;
          }
        }

        return true;
      });

      return requiredFieldsValid &&
          planSelectionValid &&
          ageValid &&
          primaryDobValid &&
          primaryIdValid &&
          primaryGenderTitleValid &&
          spouseLimitValid &&
          beneficiariesValid;
    }
  },

  mounted() {
    if (!this.paymentCompleted) {
      this.loadInitialData();
      this.checkIfHEW();
    }
  },

  methods: {
    // ==================== FULL NAME UPDATE METHODS ====================

    updateFullName() {
      const parts = [
        this.memberForm.firstName?.trim(),
        this.memberForm.middleName?.trim(),
        this.memberForm.lastName?.trim()
      ].filter(part => part);

      this.memberForm.fullName = parts.join(' ');
    },

    updateBeneficiaryFullName(index) {
      const beneficiary = this.beneficiaries[index];
      const parts = [
        beneficiary.firstName?.trim(),
        beneficiary.middleName?.trim(),
        beneficiary.lastName?.trim()
      ].filter(part => part);

      beneficiary.fullName = parts.join(' ');
    },

    // ==================== AUTHENTICATION & INITIALIZATION ====================

    checkAuth() {
      const token = localStorage.getItem("authToken");
      if (!token) {
        router.push('/login');
      }
    },

    checkIfHEW() {
      const userRole = sessionStorage.getItem("userRole");
      if (userRole === 'HEW') {
        this.memberForm.isHEWRegistration = true;
      }
    },

    async loadInitialData() {
      await this.loadAvailablePlans();
      await this.loadCounties();
    },

    // ==================== CURRENCY FORMATTING ====================

    formatCurrency(amount) {
      if (amount === null || amount === undefined) return `${this.currencySymbol} 0`;
      return `${this.currencySymbol} ${new Intl.NumberFormat().format(amount)}`;
    },

    // ==================== PLAN MANAGEMENT ====================

    async loadAvailablePlans() {
      if (this.paymentCompleted || this.availablePlans.length > 0) {
        return;
      }

      this.planLoading = true;

      const data = {
        "function": "getAvailablePlans"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.availablePlans = res.data.data.plans || [];

          if (this.availablePlans.length === 1) {
            this.selectPlan(this.availablePlans[0]);
          }
        } else {
          this.showAlert('failed', 'Failed to load available plans');
        }
      } catch (e) {
        console.error("Failed to load plans:", e);
        this.showAlert('failed', 'Error loading available plans');
      } finally {
        this.planLoading = false;
      }
    },

    selectPlan(plan) {
      this.memberForm.selectedPlanId = plan.plan_id;
      this.selectedPlan = plan;

      if (this.memberForm.enrollmentType === 'cbhi_member' && this.beneficiaries.length === 0) {
        this.beneficiaries = [{
          title: '',
          firstName: '',
          middleName: '',
          lastName: '',
          fullName: '',
          gender: '',
          dateOfBirth: '',
          relationship: '',
          idNumber: '',
          hasDisability: false,
          disabilityNotes: '',
          isDependent: false,
          dependencyNotes: '',
          ageWarning: '',
          relationshipError: ''
        }];
      }
    },

    clearPlanSelection() {
      this.memberForm.selectedPlanId = null;
      this.selectedPlan = null;
    },

    getPlanTypeBadgeClass(planType) {
      const typeClasses = {
        'basic': 'bg-info',
        'standard': 'bg-primary',
        'premium': 'bg-success',
        'family': 'bg-warning text-dark'
      };
      return typeClasses[planType.toLowerCase()] || 'bg-secondary';
    },

    getBeneficiaryRuleValue(plan, ...keys) {
      if (!plan || !plan.beneficiary_rules) {
        return 'N/A';
      }

      let value = plan.beneficiary_rules;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return 'N/A';
        }
      }

      if (value === 'unlimited' || value === 999) {
        return 'Unlimited';
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      return value;
    },

    formatSpouseCount(value) {
      if (value === 'unlimited' || value === 'Unlimited' || value === 999) {
        return 'Unlimited';
      }
      return value;
    },

    getChildAgeThreshold(plan) {
      if (!plan || !plan.beneficiary_rules) {
        return 18;
      }
      const age = plan.beneficiary_rules.free_immediate_family?.children_under_age;
      return age || 18;
    },

    getSpouseLimit(plan) {
      if (!plan || !plan.beneficiary_rules) {
        return 1;
      }
      const spouses = plan.beneficiary_rules.free_immediate_family?.included_spouses;
      if (spouses === 'unlimited' || spouses === 999) {
        return 'unlimited';
      }
      return spouses || 1;
    },

    getSpouseRuleText(plan) {
      const limit = this.getSpouseLimit(plan);
      if (limit === 'unlimited') {
        return 'unlimited spouses allowed';
      }
      return `first ${limit} spouse${limit > 1 ? 's' : ''} free, additional charged at ${(parseFloat(plan.additional_member_rate) * 100).toFixed(0)}%`;
    },

    formatImmediateFamilyRules(plan) {
      if (!plan || !plan.beneficiary_rules) {
        return 'First spouse and children under 18';
      }

      const rules = plan.beneficiary_rules.free_immediate_family;
      const spouses = rules.included_spouses === 'unlimited' ? 'unlimited spouses' :
          (rules.included_spouses === 1 ? 'first spouse' : `first ${rules.included_spouses} spouses`);
      const children = `children under ${rules.children_under_age}`;

      return `${spouses} and ${children}`;
    },

    formatBeneficiaryRulesSummary(plan) {
      if (!plan || !plan.beneficiary_rules) {
        return '1 spouse + children under 18';
      }

      const rules = plan.beneficiary_rules.free_immediate_family;
      const spouses = rules.included_spouses === 'unlimited' ? '∞ spouses' :
          `${rules.included_spouses} spouse${rules.included_spouses > 1 ? 's' : ''}`;
      const children = `children <${rules.children_under_age}`;

      return `${spouses} + ${children}`;
    },

    getSampleScenarios() {
      return [
        { label: 'Principal Only', calculation: 'principal_only' },
        { label: 'Principal + Spouse', calculation: 'principal_plus_spouse' },
        { label: 'Principal + Spouse + 2 Children', calculation: 'principal_plus_immediate_family' },
        { label: 'Additional Spouse', calculation: 'additional_spouse' },
        { label: 'Adult Child (18+)', calculation: 'adult_child_rate' },
        { label: 'Other Relative', calculation: 'additional_relative_rate' }
      ];
    },

    calculateScenarioCost(plan, calculationType) {
      if (plan.is_free) {
        return 'FREE';
      }

      if (!plan.pricing || !plan.pricing[calculationType]) {
        return 'N/A';
      }

      const cost = plan.pricing[calculationType];
      return this.formatCurrency(cost);
    },

    getFeatureValue(plan, featureKey) {
      if (!plan || !plan.features || !featureKey) {
        return false;
      }
      const value = plan.features[featureKey];
      return typeof value === 'boolean' ? value : false;
    },

    formatFeatureValue(value) {
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'string') {
        return value;
      }
      return value ? 'Yes' : 'No';
    },

    formatFeatureName(key) {
      if (!key || typeof key !== 'string') {
        return '';
      }
      return key.replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    // ==================== DISABILITY & DEPENDENCY METHODS ====================

    isAdultChild(beneficiary) {
      if (!beneficiary.dateOfBirth || beneficiary.relationship !== 'child') {
        return false;
      }
      const age = this.calculateAge(beneficiary.dateOfBirth);
      const childAgeThreshold = this.selectedPlan ? this.getChildAgeThreshold(this.selectedPlan) : 18;
      return age >= childAgeThreshold;
    },

    isAdultChildQualifyingForFree(beneficiary) {
      if (!this.isAdultChild(beneficiary)) {
        return false;
      }
      return beneficiary.hasDisability || beneficiary.isDependent;
    },

    isImmediateFamilyWithSpecialCase(beneficiary, beneficiaryIndex) {
      if (!beneficiary.relationship || !beneficiary.dateOfBirth || !this.selectedPlan) {
        return false;
      }

      const childAgeThreshold = this.getChildAgeThreshold(this.selectedPlan);
      const spouseLimit = this.getSpouseLimit(this.selectedPlan);

      if (beneficiary.relationship === 'spouse') {
        if (spouseLimit === 'unlimited') {
          return true;
        }

        let spousesBefore = 0;
        for (let i = 0; i < beneficiaryIndex; i++) {
          if (this.beneficiaries[i].relationship === 'spouse') {
            spousesBefore++;
          }
        }

        return spousesBefore < spouseLimit;
      }

      if (beneficiary.relationship === 'child') {
        const age = this.calculateAge(beneficiary.dateOfBirth);

        if (age < childAgeThreshold) {
          return true;
        }

        if (age >= childAgeThreshold) {
          return beneficiary.hasDisability || beneficiary.isDependent;
        }
      }

      return false;
    },

    onDisabilityStatusChange(index) {
      const beneficiary = this.beneficiaries[index];

      if (beneficiary.hasDisability) {
        this.showAlert('info', `Beneficiary ${index + 1} will be treated as FREE immediate family member due to disability`);
      } else {
        beneficiary.disabilityNotes = '';
      }

      this.recalculatePremiumPreview();
    },

    onDependencyStatusChange(index) {
      const beneficiary = this.beneficiaries[index];

      if (beneficiary.isDependent) {
        this.showAlert('info', `Beneficiary ${index + 1} will be treated as FREE immediate family member as dependent`);
      } else {
        beneficiary.dependencyNotes = '';
      }

      this.recalculatePremiumPreview();
    },

    recalculatePremiumPreview() {
      this.$forceUpdate();
    },

    getImmediateFamilyCountEnhanced() {
      if (!this.selectedPlan || !this.selectedPlan.beneficiary_rules) {
        return 0;
      }

      let count = 0;
      const childAgeThreshold = this.getChildAgeThreshold(this.selectedPlan);
      const freeSpousesCount = this.getSpouseLimit(this.selectedPlan);
      let spousesSoFar = 0;

      this.filteredBeneficiaries.forEach((b, index) => {
        if (b.relationship === 'spouse') {
          if (freeSpousesCount === 'unlimited' || spousesSoFar < freeSpousesCount) {
            count++;
            spousesSoFar++;
          }
        } else if (b.relationship === 'child' && b.dateOfBirth) {
          const age = this.calculateAge(b.dateOfBirth);

          if (age < childAgeThreshold) {
            count++;
          } else if (age >= childAgeThreshold && (b.hasDisability || b.isDependent)) {
            count++;
          }
        }
      });

      return count;
    },

    getAdditionalMemberCountEnhanced() {
      if (!this.selectedPlan || !this.selectedPlan.beneficiary_rules) {
        return 0;
      }

      let count = 0;
      const childAgeThreshold = this.getChildAgeThreshold(this.selectedPlan);
      const freeSpousesCount = this.getSpouseLimit(this.selectedPlan);
      let spousesSoFar = 0;

      this.filteredBeneficiaries.forEach(b => {
        if (b.relationship === 'spouse') {
          if (freeSpousesCount !== 'unlimited' && spousesSoFar >= freeSpousesCount) {
            count++;
          }
          spousesSoFar++;
        } else if (b.relationship === 'child' && b.dateOfBirth) {
          const age = this.calculateAge(b.dateOfBirth);

          if (age >= childAgeThreshold && !b.hasDisability && !b.isDependent) {
            count++;
          }
        } else if (['parent', 'grandparent', 'sibling', 'grandchild', 'other'].includes(b.relationship)) {
          count++;
        }
      });

      return count;
    },

    // ==================== VALIDATION ====================

    validateGenderSalutationMatch(gender, title, memberType = 'member') {
      if (!gender || !title) {
        return { valid: true, message: '' };
      }

      const maleOnlyTitles = ['Mr', 'Dr', 'Prof'];
      const femaleOnlyTitles = ['Mrs', 'Miss', 'Ms'];

      if (maleOnlyTitles.includes(title) && gender === 'female') {
        return {
          valid: false,
          message: `${title} title cannot be used with Female gender for ${memberType}`
        };
      }

      if (femaleOnlyTitles.includes(title) && gender === 'male') {
        return {
          valid: false,
          message: `${title} title cannot be used with Male gender for ${memberType}`
        };
      }

      return { valid: true, message: '' };
    },

    onPrimaryTitleChange() {
      if (this.memberForm.gender) {
        const validation = this.validateGenderSalutationMatch(
            this.memberForm.gender,
            this.memberForm.title,
            'primary member'
        );
        if (!validation.valid) {
          this.showAlert('failed', validation.message);
          this.memberForm.title = '';
        }
      }
    },

    onPrimaryGenderChange() {
      if (this.memberForm.title) {
        const validation = this.validateGenderSalutationMatch(
            this.memberForm.gender,
            this.memberForm.title,
            'primary member'
        );
        if (!validation.valid) {
          this.showAlert('failed', validation.message);
          this.memberForm.gender = '';
        }
      }
    },

    onBeneficiaryTitleChange(index) {
      const beneficiary = this.beneficiaries[index];
      if (beneficiary.gender) {
        const validation = this.validateGenderSalutationMatch(
            beneficiary.gender,
            beneficiary.title,
            `beneficiary ${index + 1}`
        );
        if (!validation.valid) {
          this.showAlert('failed', validation.message);
          beneficiary.title = '';
        }
      }
    },

    onBeneficiaryGenderChange(index) {
      const beneficiary = this.beneficiaries[index];
      if (beneficiary.title) {
        const validation = this.validateGenderSalutationMatch(
            beneficiary.gender,
            beneficiary.title,
            `beneficiary ${index + 1}`
        );
        if (!validation.valid) {
          this.showAlert('failed', validation.message);
          beneficiary.gender = '';
        }
      }
    },

    // ==================== ALERTS ====================

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    // ==================== ENROLLMENT TYPE ====================

    onEnrollmentTypeChange() {
      if (this.paymentCompleted) {
        return;
      }

      if (this.memberForm.enrollmentType === 'community_member') {
        this.beneficiaries = [];
        this.memberForm.selectedPlanId = null;
        this.selectedPlan = null;
        if (this.memberForm.wardId) {
          this.loadHouseholds();
        }
      } else {
        if (this.availablePlans.length === 0) {
          this.loadAvailablePlans();
        }

        this.beneficiaries = [{
          title: '',
          firstName: '',
          middleName: '',
          lastName: '',
          fullName: '',
          gender: '',
          dateOfBirth: '',
          relationship: '',
          idNumber: '',
          hasDisability: false,
          disabilityNotes: '',
          isDependent: false,
          dependencyNotes: '',
          ageWarning: '',
          relationshipError: ''
        }];
      }
    },

    // ==================== LOCATION MANAGEMENT ====================

    async loadHouseholds() {
      if (!this.memberForm.wardId) return;

      const data = {
        "function": "getHouseholds",
        "wardId": this.memberForm.wardId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.households = res.data.data.households || [];
        }
      } catch (e) {
        console.error("Failed to load households:", e);
      }
    },

    async loadCounties() {
      const data = {
        "function": "getCounties"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.counties = res.data.data.counties || [];
        }
      } catch (e) {
        console.error("Failed to load counties:", e);
      }
    },

    async loadSubCounties(countyId) {
      const data = {
        "function": "getSubCounties",
        "countyId": countyId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.subCounties = res.data.data.subCounties || [];
        }
      } catch (e) {
        console.error("Failed to load sub-counties:", e);
      }
    },

    async loadWards(subCountyId) {
      const data = {
        "function": "getWards",
        "subCountyId": subCountyId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.wards = res.data.data.wards || [];
        }
      } catch (e) {
        console.error("Failed to load wards:", e);
      }
    },

    async loadLocations(wardId) {
      const data = {
        "function": "getLocations",
        "wardId": wardId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.locations = res.data.data.locations || [];
        }
      } catch (e) {
        console.error("Failed to load locations:", e);
      }
    },

    async onCountyChange() {
      this.memberForm.subCountyId = '';
      this.memberForm.wardId = '';
      this.memberForm.locationId = '';
      this.subCounties = [];
      this.wards = [];
      this.locations = [];

      if (this.memberForm.countyId) {
        await this.loadSubCounties(this.memberForm.countyId);
      }
    },

    async onSubCountyChange() {
      this.memberForm.wardId = '';
      this.memberForm.locationId = '';
      this.wards = [];
      this.locations = [];

      if (this.memberForm.subCountyId) {
        await this.loadWards(this.memberForm.subCountyId);
      }
    },

    async onWardChange() {
      this.memberForm.locationId = '';
      this.locations = [];

      if (this.memberForm.wardId) {
        await this.loadLocations(this.memberForm.wardId);
      }

      if (this.memberForm.enrollmentType === 'community_member' && this.memberForm.wardId) {
        await this.loadHouseholds();
      }
    },

    async onLocationChange() {
      // Additional logic if needed when location changes
    },

    // ==================== AGE & DATE VALIDATION ====================

    getPrimaryMemberMaxDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 18);
      return date.toISOString().split('T')[0];
    },

    getPrimaryMemberMinDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 120);
      return date.toISOString().split('T')[0];
    },

    validatePrimaryMemberAge() {
      if (!this.memberForm.dateOfBirth) {
        this.primaryMemberAgeWarning = '';
        this.primaryMemberAgeClass = 'text-muted';
        return true;
      }

      const age = this.calculateAge(this.memberForm.dateOfBirth);

      if (age < 18) {
        this.primaryMemberAgeWarning = 'Primary member must be at least 18 years old';
        this.primaryMemberAgeClass = 'text-danger';
        return false;
      }

      if (age > 120) {
        this.primaryMemberAgeWarning = 'Please enter a valid date of birth';
        this.primaryMemberAgeClass = 'text-danger';
        return false;
      }

      this.primaryMemberAgeWarning = '';
      this.primaryMemberAgeClass = 'text-muted';
      return true;
    },

    // ==================== BENEFICIARIES MANAGEMENT ====================

    addBeneficiary() {
      this.beneficiaries.push({
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        fullName: '',
        gender: '',
        dateOfBirth: '',
        relationship: '',
        idNumber: '',
        hasDisability: false,
        disabilityNotes: '',
        isDependent: false,
        dependencyNotes: '',
        ageWarning: '',
        relationshipError: ''
      });
    },

    removeBeneficiary(index) {
      this.beneficiaries.splice(index, 1);
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return null;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age;
    },

    isAdultRelationship(relationship) {
      const adultRelationships = ['spouse', 'parent', 'grandparent'];
      return adultRelationships.includes(relationship);
    },

    getAgeRequirement(relationship) {
      if (relationship === 'child' || relationship === 'sibling') {
        return 'Any age allowed';
      }
      if (this.isAdultRelationship(relationship)) {
        return 'Must be 18 years or older';
      }
      if (relationship === 'grandchild') {
        return 'Any age allowed';
      }
      if (relationship === 'other') {
        return 'Any age allowed';
      }
      return '';
    },

    getMaxDate(relationship) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },

    getMinDate(relationship) {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 120);
      return date.toISOString().split('T')[0];
    },

    onBeneficiaryRelationshipChange(index) {
      const beneficiary = this.beneficiaries[index];

      beneficiary.relationshipError = '';
      beneficiary.ageWarning = '';

      if (beneficiary.dateOfBirth) {
        this.validateBeneficiaryAge(index);
      }
    },

    validateBeneficiaryAge(index) {
      const beneficiary = this.beneficiaries[index];

      if (!beneficiary.dateOfBirth || !beneficiary.relationship) {
        beneficiary.ageWarning = '';
        return true;
      }

      const dobDate = new Date(beneficiary.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dobDate.setHours(0, 0, 0, 0);

      if (dobDate > today) {
        beneficiary.ageWarning = 'Date of birth cannot be in the future';
        return false;
      }

      const age = this.calculateAge(beneficiary.dateOfBirth);

      if (age > 120) {
        beneficiary.ageWarning = 'Please enter a valid date of birth';
        return false;
      }

      if (this.isAdultRelationship(beneficiary.relationship) && age < 18) {
        beneficiary.ageWarning = `${this.capitalizeFirst(beneficiary.relationship)} must be 18 years or older`;
        return false;
      }

      beneficiary.ageWarning = '';
      return true;
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    // ==================== FORM VALIDATION & SUBMISSION ====================

    validateForm() {
      if (!this.memberForm.title || !this.memberForm.firstName ||
          !this.memberForm.lastName || !this.memberForm.dateOfBirth ||
          !this.memberForm.gender || !this.memberForm.countyId ||
          !this.memberForm.subCountyId || !this.memberForm.wardId ||
          !this.memberForm.address) {
        this.showAlert('failed', 'Please fill all required fields');
        return false;
      }

      this.updateFullName();

      const primaryGenderValidation = this.validateGenderSalutationMatch(
          this.memberForm.gender,
          this.memberForm.title,
          'primary member'
      );
      if (!primaryGenderValidation.valid) {
        this.showAlert('failed', primaryGenderValidation.message);
        return false;
      }

      if (this.memberForm.enrollmentType === 'cbhi_member' && !this.memberForm.selectedPlanId) {
        this.showAlert('failed', 'Please select a health insurance plan');
        return false;
      }

      if (!this.validatePrimaryMemberAge()) {
        this.showAlert('failed', this.primaryMemberAgeWarning);
        return false;
      }

      const primaryDob = new Date(this.memberForm.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      primaryDob.setHours(0, 0, 0, 0);

      if (primaryDob > today) {
        this.showAlert('failed', 'Primary member date of birth cannot be in the future');
        return false;
      }

      if (this.memberForm.enrollmentType === 'cbhi_member') {
        if (!this.memberForm.nationalId || this.memberForm.nationalId.trim() === '') {
          this.showAlert('failed', 'ID Number is required for CBHI members');
          return false;
        }
      }

      if (this.memberForm.enrollmentType === 'cbhi_member') {
        for (let i = 0; i < this.beneficiaries.length; i++) {
          const beneficiary = this.beneficiaries[i];

          if (beneficiary.relationshipError) {
            this.showAlert('failed', beneficiary.relationshipError);
            return false;
          }

          if (beneficiary.firstName || beneficiary.lastName || beneficiary.fullName) {
            this.updateBeneficiaryFullName(i);

            if (!beneficiary.title || !beneficiary.firstName ||
                !beneficiary.lastName ||
                !beneficiary.gender || !beneficiary.dateOfBirth || !beneficiary.relationship) {
              this.showAlert('failed', `Please fill all required fields for beneficiary ${i + 1}`);
              return false;
            }

            const beneficiaryGenderValidation = this.validateGenderSalutationMatch(
                beneficiary.gender,
                beneficiary.title,
                `beneficiary ${i + 1}`
            );
            if (!beneficiaryGenderValidation.valid) {
              this.showAlert('failed', beneficiaryGenderValidation.message);
              return false;
            }

            const beneficiaryDob = new Date(beneficiary.dateOfBirth);
            const todayCheck = new Date();
            todayCheck.setHours(0, 0, 0, 0);
            beneficiaryDob.setHours(0, 0, 0, 0);

            if (beneficiaryDob > todayCheck) {
              this.showAlert('failed', `Date of birth cannot be in the future for beneficiary ${i + 1}`);
              return false;
            }

            if (!this.validateBeneficiaryAge(i)) {
              this.showAlert('failed', `Invalid date of birth for beneficiary ${i + 1}: ${beneficiary.ageWarning}`);
              return false;
            }

            const age = this.calculateAge(beneficiary.dateOfBirth);
            if (age > 120 || age < 0) {
              this.showAlert('failed', `Please enter a valid date of birth for beneficiary ${i + 1}`);
              return false;
            }

            if (beneficiary.relationship !== 'child') {
              if (!beneficiary.idNumber || beneficiary.idNumber.trim() === '') {
                this.showAlert('failed', `ID Number is required for beneficiary ${i + 1} (${beneficiary.relationship})`);
                return false;
              }
            }

            if (beneficiary.hasDisability && (!beneficiary.disabilityNotes || beneficiary.disabilityNotes.trim() === '')) {
              this.showAlert('failed', `Disability details are required for beneficiary ${i + 1}`);
              return false;
            }
          }
        }
      }

      return true;
    },

    validateAndSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.memberForm.householdSize = this.totalBeneficiaries;

      if (this.memberForm.enrollmentType === 'cbhi_member' && this.selectedPlan && this.selectedPlan.is_free) {
        this.registerMember().then(success => {
          if (success) {
            this.showSuccessModal = true;
          }
        });
      } else if (this.memberForm.enrollmentType === 'cbhi_member') {
        this.registerMemberAndShowPayment();
      } else {
        this.registerMember();
      }
    },

    storeRegistrationDataTemporarily() {
      this.tempRegistrationData = {
        memberForm: { ...this.memberForm },
        selectedPlan: this.selectedPlan,
        annualPremium: this.annualPremium,
        totalBeneficiaries: this.totalBeneficiaries,
        beneficiaries: JSON.parse(JSON.stringify(this.beneficiaries)),
        subCounties: [...this.subCounties],
        wards: [...this.wards]
      };
    },

    async registerMemberAndShowPayment() {
      const registered = await this.registerMember();

      if (registered) {
        const tempData = this.tempRegistrationData || {};

        const memberInfo = {
          member_id: this.registrationResult.memberId,
          cbhi_number: this.registrationResult.cbhiNumber,
          full_name: tempData.memberForm?.fullName || '',
          phone_number: tempData.memberForm?.phoneNumber || '',
          ward_name: tempData.wards?.find(w => w.ward_id === tempData.memberForm?.wardId)?.ward_name || '',
          sub_county_name: tempData.subCounties?.find(s => s.sub_county_id === tempData.memberForm?.subCountyId)?.sub_county_name || ''
        };

        this.registrationResult = {
          ...this.registrationResult,
          ...memberInfo
        };

        this.$refs.paymentModal.openModal({
          balanceDue: tempData.annualPremium || this.annualPremium,
          totalPaidPremiums: 0,
          selectedPlan: tempData.selectedPlan || this.selectedPlan,
          annualPremium: tempData.annualPremium || this.annualPremium,
          beneficiaries: tempData.beneficiaries || []
        });

        this.tempRegistrationData = null;
      }
    },

    async registerMember() {
      this.loading = true;
      this.clearAlerts();

      this.updateFullName();
      this.beneficiaries.forEach((b, index) => {
        this.updateBeneficiaryFullName(index);
      });

      const validBeneficiaries = this.beneficiaries.filter(b =>
          b.fullName && b.dateOfBirth
      ).map(b => {
        const { ageWarning, relationshipError, ...beneficiaryData } = b;
        return beneficiaryData;
      });

      const familyComposition = {
        spouse: 0,
        childrenUnder18: 0,
        additionalSpouses: 0,
        childrenOver18: 0,
        childrenWithDisability: 0,
        dependentChildren: 0,
        otherRelatives: 0
      };

      const childAgeThreshold = this.selectedPlan ? this.getChildAgeThreshold(this.selectedPlan) : 18;
      const freeSpousesCount = this.selectedPlan ? this.getSpouseLimit(this.selectedPlan) : 1;
      let spousesSoFar = 0;

      validBeneficiaries.forEach(b => {
        const age = this.calculateAge(b.dateOfBirth);
        if (b.relationship === 'spouse') {
          if (freeSpousesCount === 'unlimited' || spousesSoFar < freeSpousesCount) {
            familyComposition.spouse++;
          } else {
            familyComposition.additionalSpouses++;
          }
          spousesSoFar++;
        } else if (b.relationship === 'child') {
          if (age < childAgeThreshold) {
            familyComposition.childrenUnder18++;
          } else {
            if (b.hasDisability) {
              familyComposition.childrenWithDisability++;
            } else if (b.isDependent) {
              familyComposition.dependentChildren++;
            } else {
              familyComposition.childrenOver18++;
            }
          }
        } else {
          familyComposition.otherRelatives++;
        }
      });

      const data = {
        "function": "registerMember",
        ...this.memberForm,
        "beneficiaries": this.memberForm.enrollmentType === 'cbhi_member' ? validBeneficiaries : [],
        "familyComposition": familyComposition,
        "collectionPoint": sessionStorage.getItem("hospitalName") || "Main Office"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.registrationResult = res.data.data;

          if (this.memberForm.enrollmentType === 'community_member' ||
              (this.selectedPlan && this.selectedPlan.is_free)) {
            this.showSuccessModal = true;
            this.completeFormReset();
          } else if (this.memberForm.enrollmentType === 'cbhi_member') {
            this.storeRegistrationDataTemporarily();
            this.completeFormReset();
          }

          return true;
        } else {
          this.showAlert('failed', res.data.message);
          return false;
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Registration failed');
        return false;
      } finally {
        this.loading = false;
      }
    },

    completeFormReset() {
      const isHEW = this.memberForm.isHEWRegistration;

      this.memberForm = {
        enrollmentType: 'cbhi_member',
        selectedPlanId: null,
        householdId: '',
        relationshipToHead: 'head',
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        fullName: '',
        dateOfBirth: '',
        nationalId: '',
        gender: '',
        countyId: '',
        subCountyId: '',
        wardId: '',
        locationId: '',
        address: '',
        email: '',
        phoneNumber: '',
        maritalStatus: 'single',
        occupation: '',
        monthlyIncome: 0,
        householdSize: 1,
        isHEWRegistration: isHEW,
        cbhiCardIssued: false,
        cardIssueNotes: ''
      };

      this.selectedPlan = null;
      this.primaryMemberAgeWarning = '';
      this.primaryMemberAgeClass = 'text-muted';
      this.beneficiaries = [];
      this.subCounties = [];
      this.wards = [];
      this.locations = [];
      this.households = [];
      this.clearAlerts();
      this.loadCounties();
    },

    // ==================== PAYMENT & RECEIPT HANDLING ====================

    onPaymentRecorded(data) {
      this.showAlert('success', data.message);
      this.paymentCompleted = true;

      if (data.payment && data.payment.payment_id) {
        this.$refs.receiptModal.openModal(data.payment);
      } else {
        setTimeout(() => {
          this.completeFormReset();
          this.paymentCompleted = false;
          router.push('/members-all');
        }, 3000);
      }
    },

    onPaymentError(data) {
      this.showAlert('failed', data.message);
    },

    onDownloadReceipt(data) {
      this.$refs.receiptModal.openModal(data.payment);
    },

    onReceiptClosed() {
      this.completeFormReset();
      this.paymentCompleted = false;
    },

    // ==================== SUCCESS MODAL HANDLING ====================

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.completeFormReset();
      router.push('/members-all');
    },

    registerAnother() {
      this.showSuccessModal = false;
      this.completeFormReset();
      this.loadAvailablePlans();
    },

    clearForm() {
      this.completeFormReset();
      this.paymentCompleted = false;
      this.registrationResult = {};
      this.tempRegistrationData = null;

      this.loadAvailablePlans();
    }
  }
};
</script>

<style scoped>
/* Plan Comparison Styles */
.comparison-table {
  font-size: 0.9rem;
}

.comparison-table .plan-feature-header {
  min-width: 200px;
  background-color: #f8f9fa !important;
  font-weight: 600;
  vertical-align: middle;
}

.comparison-table .plan-header {
  min-width: 150px;
  vertical-align: middle;
  background-color: #2c5aa0 !important;
  color: white !important;
  font-weight: 600;
}

.comparison-table .plan-type-badge {
  display: inline-block;
  margin-top: 0.25rem;
  font-size: 0.65rem;
  padding: 0.15rem 0.3rem;
  background-color: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.comparison-table .section-header td {
  background-color: #6c757d !important;
  font-weight: 600;
  color: white !important;
  padding: 0.75rem;
  border: 2px solid #dee2e6 !important;
}

.comparison-table .feature-name {
  background-color: #f8f9fa;
  font-weight: 500;
  padding-left: 1rem;
}

.comparison-table .price-badge {
  display: inline-block;
  padding: 0.4rem 0.8rem;
  background-color: #28a745;
  color: white;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.875rem;
}

.comparison-table .price-badge.secondary {
  background-color: #6c757d;
}

.comparison-table .price-badge.bg-success {
  background-color: #28a745 !important;
}

.comparison-table .coverage-amount {
  font-weight: 600;
  color: #0d6efd;
  font-size: 0.9rem;
}

.comparison-table .coverage-icon {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}

.comparison-table .coverage-text {
  font-size: 0.8rem;
  font-weight: 500;
}

.comparison-table .select-plan-btn {
  transition: all 0.2s ease;
}

.comparison-table .select-plan-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Plan Detail Cards */
.plan-detail-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.plan-detail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.plan-detail-card.border-success {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.15);
}

.plan-detail-card .pricing-summary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.5rem;
  padding: 1rem;
  margin: -0.5rem -0.5rem 1rem -0.5rem;
}

.plan-detail-card .sample-premiums {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-radius: 0.375rem;
  padding: 1rem;
  border: 1px solid #ffc107;
}

.plan-detail-card .premium-example {
  text-align: left;
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 0.25rem;
}

/* Plan Selection Cards */
.plan-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
}

.plan-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.plan-card.border-success {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}

/* Alert Styles */
.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75) !important;
}

.text-white-50 {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Disability & Dependency Styles */
.alert-info.bg-soft {
  background-color: #e7f3ff !important;
  border: 1px solid #b3d9ff !important;
}

.card.border-primary {
  border: 2px solid #0d6efd !important;
  transition: all 0.3s ease;
}

.card.border-primary:hover {
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  transform: translateY(-1px);
}

.card.border-success {
  border: 2px solid #198754 !important;
  transition: all 0.3s ease;
}

.card.border-success:hover {
  box-shadow: 0 4px 12px rgba(25, 135, 84, 0.15);
  transform: translateY(-1px);
}

/* Beneficiary Section */
.border-dashed {
  border-style: dashed !important;
}

.border-gray-5 {
  border-color: #dee2e6 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .comparison-table {
    font-size: 0.75rem;
  }

  .comparison-table .plan-header,
  .comparison-table .plan-feature-header {
    min-width: 120px;
  }
}
</style>